import { useEffect, useRef } from 'react';
import socket from '../socket';
import { showBrowserNotification } from './pushNotification';

function dispatchLocalNotification(payload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('app:notification', { detail: payload }));
}

/**
 * Shared hook to handle real-time socket notifications.
 * Only the "owner" tab triggers callbacks and browser notifications.
 */
export function useNotificationSocketHandler({
  role = 'agent',
  isNotificationOwner,
  onNewOrder,
  onOrderTaken,
  onOrderAssigned,
  onBrowserNotification,
  updatePendingOrders,
}) {
  const dedupeStorageKey = `notification-dedupe-${role}`;
  
  const seenKeysRef = useRef(new Set());

  const propsRef = useRef({});
  useEffect(() => {
    propsRef.current = {
      isNotificationOwner,
      onNewOrder,
      onOrderTaken,
      onOrderAssigned,
      onBrowserNotification,
      updatePendingOrders,
    };
  }, [isNotificationOwner, onNewOrder, onOrderTaken, onOrderAssigned, onBrowserNotification, updatePendingOrders]);

  if (seenKeysRef.current.size === 0 && typeof window !== 'undefined') {
    try {
      const stored = JSON.parse(localStorage.getItem(dedupeStorageKey) || '[]');
      stored.forEach((key) => seenKeysRef.current.add(key));
    } catch (err) {
      console.warn('Failed to load notification dedupe keys', err);
    }
  }

  const saveDedupeKey = (key) => {
    if (!key || typeof window === 'undefined') return false;
    if (seenKeysRef.current.has(key)) return true;

    seenKeysRef.current.add(key);
    
    setTimeout(() => {
      try {
        const stored = JSON.parse(localStorage.getItem(dedupeStorageKey) || '[]');
        if (!stored.includes(key)) {
          stored.push(key);
          localStorage.setItem(dedupeStorageKey, JSON.stringify(stored.slice(-200)));
        }
      } catch (err) {
        console.warn('Failed to persist notification dedupe key', err);
      }
    }, 0);
    
    return false;
  };

  useEffect(() => {
    const handleNewOrderNotification = async (notification) => {
      if (!notification) return;
      const { isNotificationOwner: isOwner, onNewOrder: cbNewOrder, onBrowserNotification: cbBrowser, updatePendingOrders: cbUpdate } = propsRef.current;

      const orderId = notification.orderId || notification._id;
      const dedupeKey = notification.tag ? `${role}-tag-${notification.tag}` : `${role}-order-${orderId}`;

      if (saveDedupeKey(dedupeKey)) {
        if (cbUpdate) cbUpdate();
        return;
      }

      if (cbNewOrder) cbNewOrder(notification);

      if (!isOwner) {
        dispatchLocalNotification({ type: 'new-order', payload: notification });
        if (cbUpdate) cbUpdate();
        return;
      }

      const payload = notification.browserNotification || {
        title: notification.title || 'New Order Available',
        body: `${notification.vendorName || 'A vendor'} has a new order for you.`,
        orderId,
        data: { url: role === 'vendor' ? '/vendor/orders' : '/agent/orders' },
        tag: `order-${orderId}`,
      };

      if (cbBrowser) cbBrowser(payload);

      // Show notification popup (this is the primary mechanism — backend web push is a fallback)
      await showBrowserNotification(payload.title, payload.body, {
        orderId: payload.orderId,
        data: payload.data,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction ?? true,
      });

      if (cbUpdate) cbUpdate();
    };

    const handleGenericBrowserNotification = async (payload) => {
      if (!payload) return;
      const { isNotificationOwner: isOwner, onBrowserNotification: cbBrowser } = propsRef.current;

      const dedupeTag = `generic-${payload.tag || payload.orderId || JSON.stringify(payload)}`;
      if (saveDedupeKey(dedupeTag)) return;

      if (!isOwner) {
        dispatchLocalNotification({ type: 'browser-notification', payload });
        return;
      }

      if (cbBrowser) cbBrowser(payload);

      await showBrowserNotification(
        payload.title || 'Notification',
        payload.body || 'You have a new update.',
        {
          orderId: payload.orderId,
          data: payload.data,
          tag: payload.tag,
          requireInteraction: payload.requireInteraction ?? true,
        }
      );
    };

    const handleOrderTaken = (data) => {
      if (propsRef.current.onOrderTaken) propsRef.current.onOrderTaken(data);
    };

    const handleOrderAssigned = async (data) => {
      const { isNotificationOwner: isOwner, onOrderAssigned: cbAssigned, updatePendingOrders: cbUpdate } = propsRef.current;
      if (cbAssigned) cbAssigned(data);

      if (!isOwner) {
        if (cbUpdate) cbUpdate();
        return;
      }

      await showBrowserNotification(
        data?.title || 'New Order Received',
        data?.body || (role === 'vendor' ? 'A new order has been assigned to you.' : 'An order has been assigned to you.'),
        {
          orderId: data?.orderId,
          data: { url: role === 'vendor' ? '/vendor/orders' : '/agent/orders' },
          tag: `assigned-${data?.orderId ?? Date.now()}`,
          requireInteraction: true,
          vibrate: [],
        }
      );

      if (cbUpdate) cbUpdate();
    };

    socket.on('new_order_notification', handleNewOrderNotification);
    socket.on('browser_notification', handleGenericBrowserNotification);
    socket.on('order_taken_notification', handleOrderTaken);
    socket.on('order_assigned', handleOrderAssigned);

    return () => {
      socket.off('new_order_notification', handleNewOrderNotification);
      socket.off('browser_notification', handleGenericBrowserNotification);
      socket.off('order_taken_notification', handleOrderTaken);
      socket.off('order_assigned', handleOrderAssigned);
    };
  }, [role]);
}